<template>
    <IonPage class="surface-primary">
        <IonHeader class="ion-no-border">
            <AppHeader
                ref="headerMenu"
                :has-scrolled="contentScrollY > 2"
                :hide-back="hideBack"
                style="--beyond-header-height: 64px"
            >
                <template #title>
                    {{ title }}
                </template>
            </AppHeader>
        </IonHeader>
        <IonContent
            scroll-events
            @ion-scroll="contentScrollY = $event.detail.scrollTop"
        >
            <!-- Position the refresher under the app header in the fixed slot -->
            <IonRefresher
                slot="fixed"
                mode="md"
                @ion-refresh="doRefresh"
            >
                <IonRefresherContent />
            </IonRefresher>
            <div v-if="showLoading || showError">
                <div class="flex h-full flex-col items-center">
                    <div
                        v-if="showLoading"
                        class="mt-30 py-10 my-auto"
                    >
                        <UILoading class="w-12 h-12" />
                    </div>
                    <div
                        v-else
                        class="w-full px-4"
                    >
                        <Error
                            :axios-error="error"
                        />
                    </div>
                </div>
            </div>
            <component
                :is="widgetPageComponents[widgetPage.type]"
                v-else
                :widget-page-id="widgetPageId"
                @widget-page-id-change="emit('widgetPageIdChange', $event)"
            />
        </IonContent>
    </IonPage>
</template>

<script setup>
    import widgetPageComponents from '@/constants/widgetPageComponents'

    import dependencyStatus from '@/constants/dependencyStatus'
    import { useWidgetPagesStore } from '@/stores/widgetPages'
    import { usePlatformAuthStore } from '@/stores/platformAuth'

    const emit = defineEmits(['widgetPageIdChange'])

    const props = defineProps({
        widgetPageId: String,
        title: {
            type: String,
            default: 'Widget',
        },
        hideBack: {
            type: Boolean,
            default: false,
        },
    })

    const widgetPagesStore = useWidgetPagesStore()
    const platformAuthStore = usePlatformAuthStore()

    const { widgetPages } = storeToRefs(widgetPagesStore)

    const refreshing = ref(false)
    const contentScrollY = ref(0)
    const headerMenu = useTemplateRef('headerMenu')

    provide('headerMenu', headerMenu)

    const widgetPageMetadata = computed(() => {
        return widgetPages.value?.[props.widgetPageId]
    })

    const widgetPage = computed(() => {
        return widgetPageMetadata.value?.data
    })

    const error = computed(() => {
        return widgetPageMetadata.value?.error
    })

    const showLoading = computed(() => {
        // If there's an error we should only show loading if we're refreshing
        if (error.value) {
            return !!refreshing.value
        }

        // The widget has it's own refreshing state
        if (refreshing.value && widgetPage.value) {
            return false
        }

        return !widgetPage.value || widgetPageMetadata.value?.status === dependencyStatus.pending
    })

    const showError = computed(() => {
        if (showLoading.value) {
            return false
        }
        return !!error.value
    })

    const refresh = async () => {
        if (refreshing.value) {
            return
        }
        refreshing.value = true

        events.emit('refreshing-widget-page', props.widgetPageId)
        return widgetPagesStore.refreshWidgetPage(props.widgetPageId)
            .then(() => {
                events.emit('refreshed-widget-page', props.widgetPageId)
            })
            .catch(() => {
                events.emit('refresh-widget-page-failed', props.widgetPageId)
            })
            .finally(() => {
                refreshing.value = false
            })
    }

    const doRefresh = (e) => {
        refresh()
            .finally(() => {
                e.target.complete()
            })
    }

    onMounted(() => {
        platformAuthStore.events.on('login', refresh)
    })

    onUnmounted(() => {
        platformAuthStore.events.off('login', refresh)
    })

</script>
